/**
 * The three operator dialogs for the source-of-truth switch.
 *
 * Extracted verbatim from RdsHealthPanel.js so the migration layout and
 * the legacy panel share ONE implementation. Every word of the refusal
 * handling and the precondition checklist lives in sourceModeErrors.js.
 *
 *   IndependenceDialog   "Enable RDS?"           -- the forward switch
 *   UseSupabaseOnlyDialog "Use Supabase only?"   -- the way off RDS: mirror
 *                         (re-clone RDS from Supabase) or Supabase only
 *                         (pause RDS, mirror nothing)
 *   MirrorRollbackDialog "Return to mirror mode?" -- the rollback (legacy panel)
 *   CatchupOffDialog     "Turn catch-up off?"     -- needs a note
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Button, Input, Checkbox, Spinner, Radio, RadioGroup,
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter,
} from "@chakra-ui/react";
import { getSourceModeStatus } from "../../../utils/rdsPhi";
import {
  CONFIRM_INDEPENDENT, CONFIRM_MIRROR, CONFIRM_SUPABASE_ONLY, CheckRow, preconditionRows,
  repairableTables, sourceModeErrorMessage,
} from "./sourceModeErrors";

/**
 * "Enable RDS" (make RDS independent). Everything RDS will refuse is shown before the
 * person types the word: the preconditions, the blockers RDS lists (red;
 * the ones `force` cannot override are marked), and the two steps -- a
 * final full mirror sync so RDS starts from Supabase's latest rows, then
 * the switch. `force` + a note is only offered while forceable blockers
 * remain, so it cannot be ticked idly.
 */
export function IndependenceDialog({ isOpen, onClose, onSync, onSubmit, onRepair, busy, enabling = false }) {
  // `enabling`: RDS is paused (Supabase only) and this turns it back on. No
  // final mirror sync is offered -- RDS refuses it while paused and it would
  // overwrite the data RDS kept; catch-up copies what changed instead.
  // The button that opens this dialog says "Enable RDS" in both states, so
  // the dialog does too; `enabling` still decides the body (no final
  // mirror sync while paused).
  const action = "Enable RDS";
  const cancelRef = useRef(null);
  const [repairArmed, setRepairArmed] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catchup, setCatchupChecked] = useState(true);
  const [force, setForce] = useState(false);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setStatusError(null);
    try {
      setStatus(await getSourceModeStatus());
    } catch (err) {
      setStatus(null);
      setStatusError(sourceModeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fresh preconditions every time the dialog opens; the typed word and
  // the force tick never survive a close, the catch-up default does.
  useEffect(() => {
    if (!isOpen) return;
    setConfirm("");
    setForce(false);
    setNote("");
    setCatchupChecked(true);
    setRepairArmed(null);
    loadStatus();
  }, [isOpen, loadStatus]);

  const blockers = status?.blockers || [];
  const forceable = status?.forceable_blockers || [];
  const hardBlockers = blockers.filter((b) => !forceable.includes(b));
  const softBlockers = blockers.filter((b) => forceable.includes(b));
  // Force is offered only while a forceable blocker remains -- and only
  // then is it sent. Once "Run final mirror sync" (or Re-check) clears the
  // last one, a tick left from before has nothing to force: drop it, so
  // the switch is not audited as ":forced" when nothing was forced.
  const forceOffered = softBlockers.length > 0;
  useEffect(() => {
    if (!forceOffered) setForce(false);
  }, [forceOffered]);
  const forceValid = !forceOffered || (force && note.trim().length > 0);
  const canSubmit = !!status && hardBlockers.length === 0 && forceValid
    && confirm === CONFIRM_INDEPENDENT && !busy;

  const runFinalSync = async () => {
    await onSync();
    await loadStatus();
  };

  // "no primary key on id" is the one hard blocker the dialog can fix
  // itself: RDS recreates the table keyed on id from Supabase (mirror mode
  // only). Two clicks -- arm, then confirm -- because the copy is dropped
  // and refilled, which takes minutes and briefly sends reads to Supabase.
  const repairTables = repairableTables(status?.preconditions);
  const runRepair = async (table) => {
    setRepairArmed(null);
    await onRepair(table);
    await loadStatus();
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered size="lg">
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="rds-independence-dialog">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">{action}?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3} fontSize="sm">
              {enabling && (
                <Text data-testid="rds-enable-intro">
                  RDS comes back as the source of truth with the data it kept while paused. Nothing is mirrored
                  from Supabase first; with catch-up on, the rows that changed in Supabase meanwhile are copied in.
                  Review files changed meanwhile in Sync RDS ⇄ Supabase.
                </Text>
              )}
              <Text>
                The whole-table mirror from Supabase stops. PHI is read from RDS and written to RDS at once;
                Supabase keeps serving non-PHI columns and sign-in. This is reversible (Use Supabase only).
              </Text>
              <Text fontSize="xs" color="orange.700">
                Gap until plan §24.1: the backend still writes some PHI to Supabase only (SMS, meds scans,
                form submissions, roster deletes). Catch-up reconciliation copies those changed rows into RDS by
                id; keep it on until those writers are routed to RDS. Documents and Storage buckets stay
                Supabase-truth mirrors either way.
              </Text>

              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>PRECONDITIONS (from RDS)</Text>
                {loading && <HStack><Spinner size="xs" /><Text fontSize="xs">Checking with RDS…</Text></HStack>}
                {statusError && (
                  <Text fontSize="xs" color="red.600" data-testid="rds-independence-status-error">{statusError}</Text>
                )}
                {status && (
                  <VStack align="stretch" spacing={1}>
                    {preconditionRows(status.preconditions).map((row) => (
                      <CheckRow key={row.label} {...row} />
                    ))}
                    {blockers.length === 0 ? (
                      <Text fontSize="xs" color="green.700" data-testid="rds-independence-ready">
                        All preconditions met.
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={0} data-testid="rds-independence-blockers">
                        {blockers.map((b) => (
                          <Text key={b} fontSize="xs" color="red.700">
                            • {b}{forceable.includes(b) ? "" : " (cannot be forced)"}
                          </Text>
                        ))}
                      </VStack>
                    )}
                    {repairTables.length > 0 && onRepair && (
                      <Box mt={1} data-testid="rds-independence-repair">
                        <Text fontSize="xs" color="gray.600">
                          Repair from here: RDS drops its copy of the table in every tenant and refills it from
                          Supabase keyed on id (a few minutes; reads fall back to Supabase meanwhile). Mirror mode only.
                        </Text>
                        <HStack mt={1} spacing={2} flexWrap="wrap">
                          {repairTables.map((table) => (repairArmed === table ? (
                            <HStack key={table} spacing={2}>
                              <Text fontSize="xs" color="red.700">Recreate {table} in every tenant?</Text>
                              <Button size="xs" colorScheme="red" onClick={() => runRepair(table)}
                                      isDisabled={!!busy} data-testid={`rds-independence-repair-confirm-${table}`}>
                                Yes, repair
                              </Button>
                              <Button size="xs" variant="ghost" onClick={() => setRepairArmed(null)}>Cancel</Button>
                            </HStack>
                          ) : (
                            <Button key={table} size="xs" variant="outline" colorScheme="red"
                                    onClick={() => setRepairArmed(table)} isLoading={busy === "repair"}
                                    loadingText="Repairing" isDisabled={!!busy && busy !== "repair"}
                                    data-testid={`rds-independence-repair-${table}`}>
                              Repair keys: {table}
                            </Button>
                          )))}
                        </HStack>
                      </Box>
                    )}
                  </VStack>
                )}
              </Box>

              {!enabling && (
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>1. FINAL FULL REFRESH</Text>
                <HStack>
                  <Button size="xs" variant="outline" onClick={runFinalSync} isLoading={busy === "sync"}
                          isDisabled={!!busy && busy !== "sync"} loadingText="Syncing"
                          data-testid="rds-independence-sync">
                    Run final mirror sync
                  </Button>
                  <Button size="xs" variant="ghost" onClick={loadStatus} isDisabled={loading || !!busy}
                          data-testid="rds-independence-recheck">
                    Re-check
                  </Button>
                </HStack>
              </Box>
              )}

              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>{enabling ? "SWITCH" : "2. SWITCH"}</Text>
                {/* testids ride on the <input> (inputProps) so tests can read checked/disabled */}
                <Checkbox size="sm" isChecked={catchup} onChange={(e) => setCatchupChecked(e.target.checked)}
                          inputProps={{ "data-testid": "rds-independence-catchup" }}>
                  Keep catch-up reconciliation on (recommended until the backend writers are routed to RDS, plan §24.1)
                </Checkbox>
                {forceOffered && (
                  <VStack align="stretch" spacing={1} mt={2}>
                    <Checkbox size="sm" isChecked={force} onChange={(e) => setForce(e.target.checked)}
                              inputProps={{ "data-testid": "rds-independence-force" }}>
                      Force past the blockers that allow it (a note is required)
                    </Checkbox>
                    <Input size="sm" placeholder="why this is safe right now" value={note}
                           onChange={(e) => setNote(e.target.value)} data-testid="rds-independence-note" />
                  </VStack>
                )}
                <Input size="sm" mt={2} placeholder={`type ${CONFIRM_INDEPENDENT} to confirm`} value={confirm}
                       onChange={(e) => setConfirm(e.target.value)} data-testid="rds-independence-confirm" />
              </Box>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={busy === "source-mode"}>Cancel</Button>
            <Button
              colorScheme="green"
              ml={3}
              onClick={() => onSubmit({ confirm, note: note.trim(), catchup, force: forceOffered && force })}
              isLoading={busy === "source-mode"}
              loadingText="Switching"
              isDisabled={!canSubmit}
              data-testid="rds-independence-submit"
            >
              {action}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/**
 * "Return to mirror mode". No preconditions: rollback must always be
 * possible. The warning is RDS's own (rows RDS holds that Supabase no
 * longer has are pruned on the next full sync), repeated before the word
 * is typed because the prune is the one irreversible part.
 */
export function MirrorRollbackDialog({ isOpen, onClose, onSubmit, busy }) {
  const cancelRef = useRef(null);
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (isOpen) { setConfirm(""); setNote(""); } }, [isOpen]);
  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="rds-mirror-dialog">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Return to mirror mode?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3} fontSize="sm">
              <Text>
                Supabase becomes the source of truth again and the whole-table mirror resumes on the next sync.
              </Text>
              <Text fontSize="xs" color="orange.700">
                Supabase holds every write made through the app (dual-write), so nothing written through the app
                is lost — but rows RDS holds that Supabase no longer has will be pruned by that sync. The overlay
                flags keep their current setting.
              </Text>
              <Input size="sm" placeholder="note for the audit log (optional)" value={note}
                     onChange={(e) => setNote(e.target.value)} data-testid="rds-mirror-note" />
              <Input size="sm" placeholder={`type ${CONFIRM_MIRROR} to confirm`} value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} data-testid="rds-mirror-confirm" />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={busy === "source-mode"}>Cancel</Button>
            <Button colorScheme="orange" ml={3} isLoading={busy === "source-mode"} loadingText="Switching"
                    isDisabled={confirm !== CONFIRM_MIRROR || !!busy}
                    onClick={() => onSubmit({ confirm, note: note.trim() })}
                    data-testid="rds-mirror-submit">
              Return to mirror mode
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/** The two ways off RDS "Use Supabase only" offers. */
export const SUPABASE_ONLY_OPTIONS = [
  {
    mode: "mirror",
    confirm: CONFIRM_MIRROR,
    title: "Mirror mode — replace RDS with Supabase",
    body: "Supabase becomes the source of truth again and RDS is re-cloned from it: PHI tables, storage buckets "
      + "and documents. Rows and files RDS holds that Supabase does not have are pruned. Files changed only in RDS "
      + "are pushed to Supabase first; a bucket with a conflict is held (not re-cloned) until you resolve it in "
      + "Sync RDS ⇄ Supabase.",
    submit: "Switch to mirror mode",
    tone: "orange",
  },
  {
    mode: "supabase_only",
    confirm: CONFIRM_SUPABASE_ONLY,
    title: "Supabase only — pause RDS, mirror nothing",
    body: "The app reads and writes Supabase alone and RDS is disabled: no mirror, no catch-up, no file sync, and "
      + "nothing in RDS is replaced or deleted. Come back later with Enable RDS (catch-up copies the rows "
      + "that changed in Supabase meanwhile; review files in Sync RDS ⇄ Supabase).",
    submit: "Use Supabase only",
    tone: "blue",
  },
];

/**
 * "Use Supabase only". Two ways off RDS, neither with preconditions (the way
 * back must always be possible): mirror (re-clone RDS from Supabase, which
 * prunes what Supabase lacks) or supabase_only (pause RDS and keep its data).
 * Each has its own typed word so the destructive one cannot be confirmed by
 * habit. `currentMode` "supabase_only" disables that option (already there).
 */
export function UseSupabaseOnlyDialog({ isOpen, onClose, onSubmit, busy, currentMode }) {
  const cancelRef = useRef(null);
  const paused = currentMode === "supabase_only";
  const [choice, setChoice] = useState(paused ? "mirror" : "supabase_only");
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (isOpen) {
      setChoice(paused ? "mirror" : "supabase_only");
      setConfirm("");
      setNote("");
    }
  }, [isOpen, paused]);
  const option = SUPABASE_ONLY_OPTIONS.find((o) => o.mode === choice) || SUPABASE_ONLY_OPTIONS[1];
  const pick = (mode) => { setChoice(mode); setConfirm(""); };
  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered size="lg">
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="rds-supabase-only-dialog">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Use Supabase only?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3} fontSize="sm">
              <RadioGroup value={choice} onChange={pick}>
                <VStack align="stretch" spacing={2}>
                  {SUPABASE_ONLY_OPTIONS.map((o) => {
                    const current = o.mode === "supabase_only" && paused;
                    return (
                      <Box
                        key={o.mode}
                        borderWidth="1px"
                        borderRadius="md"
                        p={3}
                        borderColor={choice === o.mode ? `${o.tone}.400` : "gray.200"}
                        bg={choice === o.mode ? `${o.tone}.50` : undefined}
                        opacity={current ? 0.6 : 1}
                        data-testid={`rds-supabase-only-option-${o.mode}`}
                      >
                        <Radio value={o.mode} isDisabled={current} colorScheme={o.tone}
                               data-testid={`rds-supabase-only-radio-${o.mode}`}>
                          <Text fontWeight="semibold">{o.title}{current ? " (current)" : ""}</Text>
                        </Radio>
                        <Text fontSize="xs" color="gray.600" mt={1} pl={6}>{o.body}</Text>
                      </Box>
                    );
                  })}
                </VStack>
              </RadioGroup>
              <Input size="sm" placeholder="note for the audit log (optional)" value={note}
                     onChange={(e) => setNote(e.target.value)} data-testid="rds-supabase-only-note" />
              <Input size="sm" placeholder={`type ${option.confirm} to confirm`} value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} data-testid="rds-supabase-only-confirm" />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={busy === "source-mode"}>Cancel</Button>
            <Button colorScheme={option.tone} ml={3} isLoading={busy === "source-mode"} loadingText="Switching"
                    isDisabled={confirm !== option.confirm || !!busy || (option.mode === "supabase_only" && paused)}
                    onClick={() => onSubmit({ mode: option.mode, confirm, note: note.trim() })}
                    data-testid="rds-supabase-only-submit">
              {option.submit}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/** RDS's own mode, in the operator's words, for the local-switch dialogs. */
function rdsOwnModeWord(vm) {
  if (vm?.paused) return "paused by its own switch";
  if (vm?.independent) return "independent — the source of truth for PHI";
  return "mirror mode";
}

/**
 * "Use Supabase only" for THIS NotesAI (backend/api/rds_local_switch.py).
 * Since 2026-10-24 this never touches RDS: only this deployment (its
 * frontend and its Django backend, collab server included) stops reading
 * and writing RDS. RDS keeps its mode, its jobs and every other server.
 * The typed word is the same SUPABASE as before, because the effect on
 * THIS app's users is the same: every PHI read and write moves to Supabase.
 */
export function LocalPauseDialog({ isOpen, onClose, onSubmit, busy, vm }) {
  const cancelRef = useRef(null);
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (isOpen) { setConfirm(""); setNote(""); } }, [isOpen]);
  const server = vm?.localSwitch?.server;
  const independent = !!vm?.independent && !vm?.paused;
  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered size="lg">
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="rds-local-pause-dialog">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Use Supabase only in this NotesAI?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3} fontSize="sm">
              <Text>
                Only <b>this NotesAI{server ? ` (${server})` : ""}</b> stops using RDS: its pages, its backend and its
                collaborative editor read and write Supabase alone, and open documents reload from Supabase's copy.
              </Text>
              <Text data-testid="rds-local-pause-scope">
                <b>RDS itself is not changed.</b> It stays {rdsOwnModeWord(vm)}, its mirror / catch-up / file
                sync keep running, and every other server keeps using it exactly as now.
              </Text>
              {independent && (
                <Text fontSize="xs" color="orange.700" data-testid="rds-local-pause-divergence">
                  RDS is the source of truth right now. While this NotesAI is on Supabase only, the PHI it writes
                  lands in Supabase and reaches RDS only through catch-up ({vm?.catchupOn ? "on" : "off"}) or
                  Sync RDS ⇄ Supabase — expect the two to differ until then. Other servers keep writing RDS directly.
                </Text>
              )}
              <Text fontSize="xs" color="gray.600">
                Come back with Enable RDS. Everyone using this NotesAI switches within about 30 seconds (open tabs
                included); nothing needs to be redeployed.
              </Text>
              <Input size="sm" placeholder="why (optional, shown on the panel)" value={note}
                     onChange={(e) => setNote(e.target.value)} data-testid="rds-local-pause-note" />
              <Input size="sm" placeholder={`type ${CONFIRM_SUPABASE_ONLY} to confirm`} value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} data-testid="rds-local-pause-confirm" />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={busy === "local-switch"}>Cancel</Button>
            <Button colorScheme="orange" ml={3} isLoading={busy === "local-switch"} loadingText="Switching"
                    isDisabled={confirm !== CONFIRM_SUPABASE_ONLY || !!busy}
                    onClick={() => onSubmit({ note: note.trim() })}
                    data-testid="rds-local-pause-submit">
              Use Supabase only here
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/**
 * "Enable RDS" for THIS NotesAI: clears the local switch, so this
 * deployment follows RDS's own mode again. Nothing is sent to RDS. When
 * RDS has paused ITSELF the dialog says so: enabling here changes nothing
 * until RDS's own mode is turned back on (the "RDS's own mode" menu).
 */
export function LocalEnableDialog({ isOpen, onClose, onSubmit, busy, vm }) {
  const cancelRef = useRef(null);
  const server = vm?.localSwitch?.server;
  const rdsPaused = !!vm?.paused;
  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="rds-local-enable-dialog">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Enable RDS in this NotesAI?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3} fontSize="sm">
              <Text>
                <b>This NotesAI{server ? ` (${server})` : ""}</b> follows RDS's own mode again — currently{" "}
                <b>{rdsOwnModeWord(vm)}</b>. Nothing is changed on RDS.
              </Text>
              {rdsPaused ? (
                <Text fontSize="xs" color="orange.700" data-testid="rds-local-enable-rds-paused">
                  RDS has paused itself, so this NotesAI will still use Supabase after this. To use RDS again,
                  turn it back on under RDS's own mode (that affects every server).
                </Text>
              ) : (
                <Text fontSize="xs" color="gray.600">
                  Open documents reload from RDS's copy; rows this NotesAI wrote to Supabase meanwhile reach RDS
                  through catch-up or Sync RDS ⇄ Supabase. Everyone switches within about 30 seconds.
                </Text>
              )}
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={busy === "local-switch"}>Cancel</Button>
            <Button colorScheme="green" ml={3} isLoading={busy === "local-switch"} loadingText="Enabling"
                    isDisabled={!!busy} onClick={() => onSubmit()} data-testid="rds-local-enable-submit">
              Enable RDS here
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/**
 * Turning catch-up OFF leaves the Supabase-only backend writers (§24.1)
 * unreconciled, so RDS demands a note and this asks for it. Turning it
 * back on needs nothing and never comes through here.
 */
export function CatchupOffDialog({ isOpen, onClose, onSubmit, busy }) {
  const cancelRef = useRef(null);
  const [note, setNote] = useState("");
  useEffect(() => { if (isOpen) setNote(""); }, [isOpen]);
  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="rds-catchup-dialog">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Turn catch-up reconciliation off?</AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3} fontSize="sm">
              <Text fontSize="xs" color="orange.700">
                Only safe once every backend writer sends PHI to RDS (plan §24.1). Until then, SMS, meds-scan and
                form-submission rows written to Supabase will no longer reach RDS.
              </Text>
              <Input size="sm" placeholder="why (required)" value={note}
                     onChange={(e) => setNote(e.target.value)} data-testid="rds-catchup-note" />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={busy === "catchup"}>Cancel</Button>
            <Button colorScheme="orange" ml={3} isLoading={busy === "catchup"}
                    isDisabled={!note.trim() || !!busy}
                    onClick={() => onSubmit(note.trim())}
                    data-testid="rds-catchup-submit">
              Turn catch-up off
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
